import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useEmail } from '../../../contexts/EmailContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentAPI } from '../../../services/api';

const { FiBuilding, FiUser, FiMail, FiPhone, FiCheck, FiArrowRight, FiBriefcase, FiShield, FiCreditCard, FiInfo, FiTag, FiLoader, FiFileText, FiX, FiCheckCircle, FiArrowLeft, FiLock, FiPlus } = FiIcons;

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(STRIPE_KEY);

const CompanyRegistrationContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { externalServices = [], subscriptionPlans = [], promoOffers = [], addCompany, incrementPromoUsage, createInvoice, addUser } = useData();
  const { sendCompanyRegistration } = useEmail();
  const { validateSubmission, markAsSubmitted, HoneypotField } = useFormSecurity('company_registration');
  const stripe = useStripe();
  const elements = useElements();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [promoError, setPromoError] = useState('');

  const [formData, setFormData] = useState({
    companyName: '',
    orgNumber: '',
    industry: '',
    employees: '',
    departmentsCount: '1',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactPassword: '',
    address: '',
    postalCode: '',
    city: '',
    selectedPlan: location.state?.selectedPlan || 'standard',
    selectedExternalServices: location.state?.selectedServices || [],
    salesAgentId: '',
    paymentMethod: 'invoice',
    termsAccepted: false
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref') || params.get('agent');
    if (refCode) {
      setFormData(prev => ({ ...prev, salesAgentId: refCode }));
    }
  }, [location.search]);

  const currentPlan = subscriptionPlans.find(p => p.id === formData.selectedPlan) || subscriptionPlans[0];

  const calculateTotal = () => {
    const count = Math.max(1, parseInt(formData.departmentsCount) || 1);
    let planPrice = currentPlan?.price || 0;
    let totalPlanPrice = planPrice * count;
    
    let servicesTotal = 0;
    formData.selectedExternalServices.forEach(srvId => {
      const srv = externalServices.find(s => s.id === srvId);
      if (srv) servicesTotal += srv.price;
    });

    let totalPrice = totalPlanPrice + servicesTotal;

    if (appliedDiscount) {
      if (appliedDiscount.type === 'percentage') {
        totalPrice = totalPrice * (1 - appliedDiscount.value / 100);
      } else {
        totalPrice = Math.max(0, totalPrice - appliedDiscount.value);
      }
    }

    return Math.round(totalPrice);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleToggleService = (serviceId) => {
    setFormData(prev => {
      const current = prev.selectedExternalServices;
      const updated = current.includes(serviceId)
        ? current.filter(id => id !== serviceId)
        : [...current, serviceId];
      return { ...prev, selectedExternalServices: updated };
    });
  };

  const handleApplyPromo = () => {
    setPromoError('');
    setAppliedDiscount(null);
    
    if (!promoCodeInput.trim()) return;

    const promo = promoOffers.find(p => p.code === promoCodeInput.toUpperCase() && p.active);
    
    if (!promo) {
      setPromoError('Ugyldig eller utløpt rabattkode.');
      return;
    }

    setAppliedDiscount(promo);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.companyName) {
        alert('Vennligst fyll ut Bedriftsnavn.');
        return false;
      }
      if (!formData.orgNumber) {
        alert('Vennligst fyll ut Organisasjonsnummer.');
        return false;
      }
    }
    if (step === 2) {
      if (!formData.contactName || !formData.contactEmail || !formData.contactPhone) {
        alert('Vennligst fyll ut Navn, E-post og Telefon.');
        return false;
      }
      if (!formData.contactPassword || formData.contactPassword.length < 6) {
        alert('Du må velge et passord på minst 6 tegn for å kunne logge inn.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSubmission()) return;
    
    if (!formData.termsAccepted) {
      setError('Du må godta vilkårene for å fullføre.');
      return;
    }

    markAsSubmitted();
    setIsSubmitting(true);
    setError('');

    try {
      const deptCount = Math.max(1, parseInt(formData.departmentsCount) || 1);

      if (formData.paymentMethod === 'card') {
        if (!stripe || !elements) {
          throw new Error("Stripe er ikke lastet korrekt.");
        }
        
        const cardElement = elements.getElement(CardElement);
        const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: formData.contactName,
            email: formData.contactEmail,
          },
        });

        if (methodError) {
          throw new Error(methodError.message);
        }

        await paymentAPI.createPaymentIntent({
          amount: calculateTotal(),
          paymentMethodId: paymentMethod.id,
          email: formData.contactEmail,
          description: `Abonnement: ${currentPlan.name} (x${deptCount}) - ${formData.companyName}`
        });
      }

      const newCompanyId = `comp_${Date.now()}`;
      const newCompanyData = {
        ...formData,
        id: newCompanyId,
        name: formData.companyName,
        active: true,
        createdAt: new Date().toISOString(),
        monthlyFee: calculateTotal(),
        subscription: formData.selectedPlan,
        external_services: formData.selectedExternalServices,
        appliedPromoCode: appliedDiscount ? appliedDiscount.code : null,
        departmentsCount: deptCount
      };

      if (typeof addCompany !== 'function') throw new Error("Systemfeil: addCompany mangler.");
      await addCompany(newCompanyData);

      if (typeof addUser === 'function') {
        await addUser({
          id: `u_${Date.now()}`,
          email: formData.contactEmail,
          password: formData.contactPassword,
          name: formData.contactName,
          role: 'admin',
          companyId: newCompanyId,
          companyName: formData.companyName,
          isActive: true
        });
      }

      if (typeof createInvoice === 'function') {
        await createInvoice(newCompanyData, [
          { description: `${currentPlan.name} (Abonnement)`, quantity: deptCount, price: currentPlan.price },
          ...formData.selectedExternalServices.map(srvId => {
            const srv = externalServices.find(s => s.id === srvId);
            return { description: srv?.name || 'Tilleggstjeneste', quantity: 1, price: srv?.price || 0 };
          })
        ]);
      }

      if (appliedDiscount && typeof incrementPromoUsage === 'function') {
        incrementPromoUsage(appliedDiscount.id);
      }

      if (typeof sendCompanyRegistration === 'function') {
        await sendCompanyRegistration(
          formData.contactEmail,
          formData.companyName,
          currentPlan.name,
          formData.contactName
        );
      }

      navigate('/company/login');
      
    } catch (err) {
      console.error('Registration Error:', err);
      setError(err.response?.data?.error?.message || err.message || 'Noe gikk galt under registrering.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, title: 'Bedrift', description: 'Informasjon om selskapet', icon: FiBuilding },
    { id: 2, title: 'Bruker', description: 'Administrator og passord', icon: FiUser },
    { id: 3, title: 'Pakke', description: 'Velg løsning', icon: FiBriefcase },
    { id: 4, title: 'Fullfør', description: 'Bekreft og betal', icon: FiCheckCircle }
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white font-sans">
      <HoneypotField />
      
      <div className="lg:w-1/3 bg-slate-900 text-white p-8 lg:p-12 flex flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <button 
            onClick={() => navigate('/')}
            className="group flex items-center space-x-2 text-slate-400 hover:text-white transition-colors mb-8 text-sm font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            <span>Tilbake til forsiden</span>
          </button>

          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-white text-xl" />
            </div>
            <span className="text-xl font-bold">HMSvarsling</span>
          </div>

          <div className="space-y-8">
            {steps.map((step) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 
                    ${isActive ? 'bg-blue-600 border-blue-600' : 
                      isCompleted ? 'bg-blue-900 border-blue-500 text-blue-400' : 
                      'bg-slate-800 border-slate-700 text-slate-500'}`}
                  >
                    {isCompleted ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={step.icon} />}
                  </div>
                  <div className={isActive ? 'opacity-100' : 'opacity-50'}>
                    <h3 className="text-sm font-bold uppercase">{step.title}</h3>
                    <p className="text-xs">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 bg-gray-50 flex flex-col h-full overflow-y-auto">
        <div className="flex-1 p-6 lg:p-16 max-w-3xl mx-auto w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Registrering</h2>

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {currentStep === 1 && (
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Bedriftsnavn *</label>
                  <input
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={e => handleInputChange('companyName', e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                    placeholder="Firmanavn AS"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Org. nummer *</label>
                    <input
                      type="text"
                      required
                      value={formData.orgNumber}
                      onChange={e => handleInputChange('orgNumber', e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl"
                      placeholder="999 888 777"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Antall avdelinger</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.departmentsCount}
                      onChange={e => handleInputChange('departmentsCount', e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl"
                      placeholder="1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Bransje</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={e => handleInputChange('industry', e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                    placeholder="Bygg og Anlegg"
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 mb-4 flex items-center gap-3">
                  <SafeIcon icon={FiInfo} className="text-xl" />
                  <p>Dette blir administratoren for kontoen. Du bruker e-post og passord for å logge inn etterpå.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ditt navn *</label>
                  <input
                    type="text"
                    required
                    value={formData.contactName}
                    onChange={e => handleInputChange('contactName', e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                    placeholder="Ola Nordmann"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">E-post (Brukernavn) *</label>
                  <input
                    type="email"
                    required
                    value={formData.contactEmail}
                    onChange={e => handleInputChange('contactEmail', e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                    placeholder="ola@bedrift.no"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Passord *</label>
                  <div className="relative">
                    <input
                      type="password"
                      required
                      value={formData.contactPassword}
                      onChange={e => handleInputChange('contactPassword', e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl"
                      placeholder="Velg et sikkert passord"
                      minLength={6}
                    />
                    <SafeIcon icon={FiLock} className="absolute right-4 top-3.5 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minst 6 tegn.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon *</label>
                  <input
                    type="tel"
                    required
                    value={formData.contactPhone}
                    onChange={e => handleInputChange('contactPhone', e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                    placeholder="900 00 000"
                  />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="font-bold text-lg mb-4">Velg Pakke</h3>
                <div className="space-y-4">
                  {subscriptionPlans.filter(p => p.active).map(plan => (
                    <div 
                      key={plan.id}
                      onClick={() => handleInputChange('selectedPlan', plan.id)}
                      className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.selectedPlan === plan.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex justify-between font-bold">
                        <span>{plan.name}</span>
                        <span>{plan.price},- /mnd</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{plan.employees}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-bold text-lg mt-8 mb-4">Tilleggstjenester</h3>
                <div className="space-y-4">
                  {/* Fjerner kategori-filtreringen slik at alle aktive tilleggstjenester vises */}
                  {externalServices.filter(s => s.active).map(service => (
                    <div 
                      key={service.id}
                      onClick={() => handleToggleService(service.id)}
                      className={`p-4 border-2 rounded-xl cursor-pointer flex justify-between items-center transition-all ${
                        formData.selectedExternalServices.includes(service.id) ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex-1 pr-4">
                        <span className="font-semibold block text-gray-900">{service.name}</span>
                        <span className="text-sm text-gray-600 block">{service.description}</span>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className="font-bold text-gray-900">{service.price},- /{service.timeframe}</span>
                      </div>
                      <div className={`ml-4 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        formData.selectedExternalServices.includes(service.id) ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
                      }`}>
                        {formData.selectedExternalServices.includes(service.id) && <SafeIcon icon={FiCheck} className="text-xs" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-gray-100 p-6 rounded-xl">
                  <h3 className="font-bold mb-4">Oversikt</h3>
                  <div className="flex justify-between mb-2 text-sm items-center">
                    <span>{currentPlan.name} (x {parseInt(formData.departmentsCount) || 1} avdelinger)</span>
                    <span>{currentPlan.price * (parseInt(formData.departmentsCount) || 1)},-</span>
                  </div>
                  {formData.selectedExternalServices.map(srvId => {
                    const srv = externalServices.find(s => s.id === srvId);
                    if (!srv) return null;
                    return (
                      <div key={srvId} className="flex justify-between mb-2 text-sm text-gray-600">
                        <span>+ {srv.name}</span>
                        <span>{srv.price},-</span>
                      </div>
                    )
                  })}
                  <div className="flex justify-between font-bold text-lg border-t border-gray-300 pt-3 mt-3">
                    <span>Totalt per måned</span>
                    <span>{calculateTotal()},-</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Rabattkode</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        placeholder="Kode" 
                        className="flex-1 px-4 py-2 border rounded-lg"
                        value={promoCodeInput}
                        onChange={e => setPromoCodeInput(e.target.value)}
                      />
                      <button 
                        type="button" 
                        onClick={handleApplyPromo}
                        className="px-4 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
                      >Bruk</button>
                    </div>
                    {promoError && <p className="text-red-500 text-sm mt-1">{promoError}</p>}
                    {appliedDiscount && <p className="text-green-600 text-sm mt-1">Rabatt lagt til!</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Partner / Selger ID</label>
                    <input 
                      type="text" 
                      placeholder="Valgfritt" 
                      className="w-full px-4 py-2 border rounded-lg"
                      value={formData.salesAgentId}
                      onChange={e => handleInputChange('salesAgentId', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-bold">Betalingsmetode</h4>
                  <div className="flex gap-4">
                    <button 
                      type="button"
                      onClick={() => handleInputChange('paymentMethod', 'invoice')}
                      className={`flex-1 p-3 border rounded-lg transition-colors ${formData.paymentMethod === 'invoice' ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'}`}
                    >
                      Faktura (EHF/PDF)
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleInputChange('paymentMethod', 'card')}
                      className={`flex-1 p-3 border rounded-lg transition-colors ${formData.paymentMethod === 'card' ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'}`}
                    >
                      Kort (Stripe)
                    </button>
                  </div>

                  {formData.paymentMethod === 'card' && (
                    <div className="p-4 border border-blue-200 bg-white rounded-lg shadow-sm mt-2">
                      <p className="text-sm text-gray-600 mb-3 font-medium">Sikker kortbetaling via Stripe</p>
                      <CardElement 
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          },
                        }} 
                      />
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" 
                      checked={formData.termsAccepted}
                      onChange={e => handleInputChange('termsAccepted', e.target.checked)}
                    />
                    <span className="text-sm font-medium text-gray-800">
                      Jeg godtar <button type="button" onClick={() => setShowTerms(true)} className="text-blue-600 underline">vilkårene</button>
                    </span>
                  </label>
                </div>

                {error && (
                  <div className="bg-red-50 p-4 rounded border border-red-200 text-red-700 text-sm">
                    <div className="flex items-center gap-2 font-bold mb-1">
                      <SafeIcon icon={FiX} /> Feil ved betaling/registrering:
                    </div>
                    {error}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-6 border-t">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(p => p - 1)}
                  className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >Tilbake</button>
              )}
              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={() => validateStep(currentStep) && setCurrentStep(p => p + 1)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg ml-auto hover:bg-blue-700 transition-colors"
                >Neste</button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2 bg-green-600 text-white rounded-lg ml-auto flex items-center gap-2 font-bold shadow-lg hover:bg-green-700 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Behandler...' : 'Fullfør & Betal'}
                  <SafeIcon icon={FiArrowRight} />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <AnimatePresence>
        {showTerms && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTerms(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="text-xl font-bold text-gray-900">Vilkår og Betingelser</h3>
                <button onClick={() => setShowTerms(false)}><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-6 overflow-y-auto text-sm text-gray-600 space-y-6 leading-relaxed">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">1. Generelt</h4>
                  <p>Disse vilkårene gjelder for bruk av HMS Varsling-tjenesten levert av Employer Brand AS. Ved å registrere en konto aksepterer du disse vilkårene i sin helhet.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">2. Abonnement og Betaling</h4>
                  <p className="mb-2">Tjenesten er et løpende abonnement som faktureres forskuddsvis per måned eller år.</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Alle priser er eksklusive merverdiavgift (MVA).</li>
                    <li>Betalingsbetingelser er 14 dager netto.</li>
                    <li>Ved forsinket betaling påløper lovbestemt forsinkelsesrente.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">3. Oppsigelse</h4>
                  <p>Abonnementet kan når som helst sies opp. <strong>Oppsigelsestiden er 3 måneder</strong>, regnet fra den 1. i påfølgende måned etter at skriftlig oppsigelse er mottatt.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">4. Databehandling (GDPR)</h4>
                  <p>Employer Brand AS opptrer som databehandler. Kunden er behandlingsansvarlig. Vi forplikter oss til å behandle alle personopplysninger i tråd med gjeldende personvernlovgivning (GDPR). Data lagres på sikre servere innenfor EU/EØS.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">5. Tjenestenivå (SLA)</h4>
                  <p>Vi tilstreber en oppetid på 99.5%. Planlagt vedlikehold vil bli varslet i god tid. Vi er ikke ansvarlige for feil som skyldes tredjepartsleverandører eller forhold utenfor vår kontroll (force majeure).</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-base">6. Endringer</h4>
                  <p>Vi forbeholder oss retten til å endre priser og vilkår med 30 dagers varsel. Prisjusteringer skjer normalt ved årsskiftet i henhold til KPI.</p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 bg-gray-50">
                <button 
                  onClick={() => {
                    handleInputChange('termsAccepted', true);
                    setShowTerms(false);
                  }}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Jeg forstår og godtar vilkårene
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function CompanyRegistration() {
  return (
    <Elements stripe={stripePromise}>
      <CompanyRegistrationContent />
    </Elements>
  );
}